import { IComboBoxOption, IDetailsRowProps, ILinkStyles, MessageBarType } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import {
  BaseDialog,
  CommonStyles,
  debounce,
  defaultPalette,
  detailsHeaderStylesBasic,
  DetailsList,
  detailsListStylesCard,
  Dropdown,
  EmptyMessage,
  getDescriptionForItemCount,
  getItemsWithEmptyItemsOfNextPage,
  IColumnWithDataType,
  IDetailsListProps,
  Link,
  linkStylesGhost,
  linkStylesPrimary,
  ListRow,
  Loader,
  loaderStylesSpinnerXLarge,
  MessageBar,
  onRenderTagColumn,
  TextField,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { CSSProperties, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  ListableFeatureSetToPermission,
  ListableProjectToPermission,
  Permission,
  PermissionState,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { CommonImageSrc, ContentsTitle, defaultPageSize, permissionTypeNameMap } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { ListCellImage } from '../ListCellImage/ListCellImage';
import {
  accessRequestListStylesDefault,
  IAccessRequestListClassNames,
  IAccessRequestListStyles,
} from './AccessRequestList.styles';

enum RequestAction {
  Approve = 'Approve',
  Reject = 'Reject',
  Revoke = 'Revoke',
  Withdraw = 'Withdraw',
}

const statusColorMap = new Map<PermissionState | undefined, string>([
  [PermissionState.GRANTED, defaultPalette.green100],
  [PermissionState.PENDING, defaultPalette.yellow100],
  [PermissionState.REJECTED, defaultPalette.red100],
  [PermissionState.REVOKED, defaultPalette.blue100],
]);

interface DialogInfo {
  messageBarType: MessageBarType;
  message: (item?: AccessRequest) => React.ReactNode;
  linkStyles: Partial<ILinkStyles>;
}

const dialogActionMap = new Map<RequestAction | undefined, DialogInfo>([
  [
    RequestAction.Reject,
    {
      messageBarType: MessageBarType.error,
      message: (item) => (
        <div>
          Are you sure you want to reject access to this {item?.isProject ? `project` : `feature set`}? <br />
          User <strong>{item?.requestorName}</strong> will lose access to the{' '}
          {item?.isProject ? `project` : `feature set`} &quot;
          <strong>{item?.isProject ? item?.name : item?.featureSetName}</strong>&quot;
        </div>
      ),
      linkStyles: {
        root: {
          borderColor: defaultPalette.red500,
          backgroundColor: defaultPalette.red500,
          color: defaultPalette.white,
        },
      },
    },
  ],
  [
    RequestAction.Revoke,
    {
      messageBarType: MessageBarType.warning,
      message: (item) => (
        <div>
          Are you sure you want to revoke access to this {item?.isProject ? `project` : `feature set`}?
          <br />
          User <strong>{item?.requestorName}</strong> will lose access to the{' '}
          {item?.isProject ? `project` : `feature set`} &quot;
          <strong>{item?.isProject ? item?.name : item?.featureSetName}</strong>&quot; as &quot;
          <strong>{item?.permission ? permissionTypeNameMap.get(item.permission) : ''}</strong>&quot;
        </div>
      ),
      linkStyles: {
        root: {
          borderColor: defaultPalette.yellow500,
          backgroundColor: defaultPalette.yellow500,
          color: defaultPalette.gray900,
        },
      },
    },
  ],
  [
    RequestAction.Approve,
    {
      messageBarType: MessageBarType.info,
      message: (item) => (
        <div>
          User <strong>{item?.requestorName}</strong> will be given access to the{' '}
          {item?.isProject ? `project` : `feature set`} &quot;
          <strong>{item?.isProject ? item?.name : item?.featureSetName}</strong>&quot; as &quot;
          <strong>{item?.permission ? permissionTypeNameMap.get(item.permission) : ''}</strong>&quot;
        </div>
      ),
      linkStyles: {
        root: {
          borderColor: defaultPalette.green500,
          backgroundColor: defaultPalette.green500,
          color: defaultPalette.white,
        },
      },
    },
  ],
  [
    RequestAction.Withdraw,
    {
      messageBarType: MessageBarType.warning,
      message: (item) => (
        <div>
          Are you sure you want to withdraw the access request for the {item?.isProject ? `project` : `feature set`}?
          <br />
          <strong>{item?.isProject ? item?.name : item?.featureSetName}</strong>&quot; as &quot;
          <strong>{item?.permission ? permissionTypeNameMap.get(item.permission) : ''}</strong>&quot;
        </div>
      ),
      linkStyles: {
        root: {
          borderColor: defaultPalette.yellow500,
          backgroundColor: defaultPalette.yellow500,
          color: defaultPalette.gray900,
        },
      },
    },
  ],
]);

const getAPI = (title: ContentsTitle, subTitle: ContentsTitle) => {
  if (title === ContentsTitle.projectPermissions) {
    if (subTitle === ContentsTitle.managePermissions) {
      return CoreService.ListManageableProjectPermissionsPage;
    } else {
      return CoreService.ListProjectPermissionsPage;
    }
  } else {
    if (subTitle === ContentsTitle.managePermissions) {
      return CoreService.ListManageableFeatureSetsPermissionsPage;
    } else {
      return CoreService.ListFeatureSetsPermissionsPage;
    }
  }
};

const getColumns = (
  subTitle: ContentsTitle,
  onAction: (action: RequestAction, item: AccessRequest) => void
): IColumnWithDataType[] => {
  const actionColumnWidth = subTitle !== ContentsTitle.myPermissions ? 265 : 150;
  const columns: IColumnWithDataType[] = [
    {
      key: 'name',
      name: 'Name',
      fieldName: 'description',
      minWidth: 100,
      data: {
        headerFieldName: 'name',
        listCellProps: {
          emptyMessage: 'No Description',
          onRenderPrefix: (item) =>
            item.name?.toLowerCase().includes('h2o') ? (
              <ListCellImage imageSrc={CommonImageSrc.logo512} />
            ) : (
              <ListCellImage iconName="DocumentSet" />
            ),
        },
      },
    },
    subTitle === ContentsTitle.myPermissions
      ? {
          key: 'author',
          name: 'Author',
          fieldName: 'authorName',
          minWidth: 100,
        }
      : {
          key: 'creator',
          name: 'Requestor',
          fieldName: 'requestorName',
          minWidth: 100,
        },
    {
      key: 'createdOn',
      name: 'Created On',
      fieldName: 'createdOn',
      minWidth: 100,
    },
    {
      key: 'lastUpdateOn',
      name: 'Last Update On',
      fieldName: 'lastUpdateOn',
      minWidth: 120,
    },
    {
      key: 'AccessType',
      name: 'Access Type',
      fieldName: 'permission',
      minWidth: 100,
    },
    {
      key: 'Status',
      name: 'Status',
      fieldName: 'state',
      minWidth: 120,
      onRender: (item: AccessRequest) => onRenderTagColumn(item, 'Status', 'state', statusColorMap.get(item.state)),
    },
    {
      key: 'reason',
      name: 'Reason',
      fieldName: 'reason',
      minWidth: 200,
      data: {
        listCellProps: { emptyMessage: '-' },
      },
    },
    {
      key: 'actions',
      name: 'Actions',
      minWidth: actionColumnWidth,
      maxWidth: actionColumnWidth,
      onRender: (item: AccessRequest) => (
        <div
          style={{
            ...(CommonStyles.flexCenterVertically as CSSProperties),
            textAlign: 'right',
            width: '100%',
          }}
        >
          <div>
            {subTitle !== ContentsTitle.myPermissions ? (
              item.state === PermissionState.PENDING ? (
                <>
                  <Link
                    styles={[
                      linkStylesGhost,
                      {
                        root: { borderColor: defaultPalette.green900, color: defaultPalette.green900, marginRight: 8 },
                      },
                    ]}
                    iconName="SkypeCircleCheck"
                    onClick={() => onAction(RequestAction.Approve, item)}
                  >
                    {RequestAction.Approve}
                  </Link>
                  <Link
                    styles={[
                      linkStylesGhost,
                      { root: { borderColor: defaultPalette.red900, color: defaultPalette.red900 } },
                    ]}
                    iconName="StatusErrorFull"
                    onClick={() => onAction(RequestAction.Reject, item)}
                  >
                    {RequestAction.Reject}
                  </Link>
                </>
              ) : (
                item.state === PermissionState.GRANTED && (
                  <Link
                    styles={[
                      linkStylesGhost,
                      { root: { borderColor: defaultPalette.yellow900, color: defaultPalette.yellow900 } },
                    ]}
                    iconName="Redo"
                    onClick={() => onAction(RequestAction.Revoke, item)}
                  >
                    {RequestAction.Revoke}
                  </Link>
                )
              )
            ) : item.state === PermissionState.PENDING ? (
              <Link styles={linkStylesGhost} onClick={() => onAction(RequestAction.Withdraw, item)}>
                {RequestAction.Withdraw}
              </Link>
            ) : null}
          </div>
        </div>
      ),
    },
  ];

  return columns;
};

const getReason = ({ state, creationReason, grantedOrRejected, revoked, promoted }: Permission): string => {
  switch (state) {
    case PermissionState.PENDING:
      return `${creationReason}`;
    case PermissionState.GRANTED:
    case PermissionState.REJECTED:
      return `${grantedOrRejected?.reason}`;
    case PermissionState.REVOKED:
      return `${revoked?.reason}`;
    case PermissionState.PROMOTED:
      return `${promoted?.reason}`;
    default:
      return '';
  }
};

export const getAccessRequestListItem = (
  entry: ListableProjectToPermission | ListableFeatureSetToPermission
): AccessRequest => {
  const projectEntry: ListableProjectToPermission = entry;
  const featureSetEntry: ListableFeatureSetToPermission = entry;
  if (projectEntry.project) {
    const { name, description } = projectEntry.project;
    return {
      ...projectEntry.permission,
      name,
      description,
      reason: getReason(entry.permission || {}),
      requestorName: entry.permission?.user?.name,
      authorName: projectEntry.project?.author?.name,
      isProject: true,
    };
  } else {
    const { projectName, name, description } = featureSetEntry.featureSet || {};
    return {
      ...featureSetEntry.permission,
      name: `${projectName} > ${name}`,
      description,
      reason: getReason(entry.permission || {}),
      requestorName: entry.permission?.user?.name,
      authorName: featureSetEntry.featureSet?.author?.name,
      featureSetName: name,
    };
  }
};

const getTargetName = (title: ContentsTitle) =>
  title === ContentsTitle.projectPermissions ? 'project' : 'feature set';

const filterOptions: IComboBoxOption[] = [
  { key: PermissionState.GRANTED, text: 'Granted', selected: true },
  { key: PermissionState.PENDING, text: 'Pending', selected: true },
  { key: PermissionState.REJECTED, text: 'Rejected', selected: true },
  { key: PermissionState.REVOKED, text: 'Revoked', selected: true },
];

export interface AccessRequest extends Permission {
  name?: string;
  description?: string;
  reason?: string;
  requestorName?: string;
  authorName?: string;
  featureSetName?: string;
  isProject?: boolean;
}

interface AccessRequestListData {
  items: AccessRequest[];
  pageToken?: string;
  pageSize?: number;
}

interface IAccessRequestListProps {
  title: ContentsTitle;
  subTitle: ContentsTitle;
  filters?: PermissionState[];
}

export const AccessRequestList = ({
  title,
  subTitle,
  filters: baseFilters = [
    PermissionState.GRANTED,
    PermissionState.PENDING,
    PermissionState.REJECTED,
    PermissionState.REVOKED,
  ],
}: IAccessRequestListProps) => {
  const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);
  const [description, setDescription] = useState('');
  const [filters, setFilters] = useState<PermissionState[]>(baseFilters);
  const [data, setData] = useState<AccessRequestListData>({
    items: [],
  });
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const refMeta = useRef<{
    isLoadingPage: boolean;
    accessRequest?: AccessRequest;
    actionType?: RequestAction;
    dialogInfo?: DialogInfo;
    reason: string;
    mounted?: boolean;
  }>({
    isLoadingPage: false,
    reason: '',
  });
  const { addToast } = useToast();
  const load = useCallback(
    async ({ items, pageToken }: AccessRequestListData = { items: [] }, hasLoading = true) => {
      hasLoading && setLoadingMessage(`Loading ${subTitle}...`);
      try {
        const pageSize = defaultPageSize;
        const { entries = [], nextPageToken } = await fsApi(getAPI(title, subTitle), {
          filters,
          pageSize,
          pageToken,
        });
        const newItems = getItemsWithEmptyItemsOfNextPage(items, entries, pageSize, getAccessRequestListItem);
        const newData = {
          items: newItems,
          pageToken: nextPageToken,
          pageSize,
        };
        if (!refMeta.current.mounted) return;
        setData({
          ...newData,
        });
        setDescription(getDescriptionForItemCount(newItems.length, pageSize, nextPageToken, subTitle));
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading ${subTitle}: `));
      } finally {
        if (!refMeta.current.mounted) return;
        if (hasLoading) setLoadingMessage('');
        refMeta.current.isLoadingPage = false;
      }
    },
    [addToast, subTitle, title, filters]
  );
  const onLoad = useMemo(() => debounce(300, load), [load]);
  const doAction = useCallback(async () => {
    const actionType = refMeta.current.actionType;
    const reason = refMeta.current.reason;
    setLoadingMessage(`Apply ${actionType} request...`);
    try {
      const { id: permissionId } = refMeta.current.accessRequest || {};
      switch (actionType) {
        case RequestAction.Approve:
          await fsApi(CoreService.ApprovePendingPermission, {
            permissionId,
            reason,
          });
          addToast({
            messageBarType: MessageBarType.success,
            message: `You have Approved access to the requested ${getTargetName(title)}`,
          });
          break;
        case RequestAction.Reject:
          await fsApi(CoreService.RejectPendingPermission, {
            permissionId,
            reason,
          });
          addToast({
            messageBarType: MessageBarType.error,
            message: `You have Rejected access to the requested ${getTargetName(title)}`,
          });
          break;
        case RequestAction.Revoke:
          await fsApi(CoreService.RevokePermission, {
            permissionId,
            reason,
          });
          addToast({
            messageBarType: MessageBarType.warning,
            message: `You have Revoked access to the requested ${getTargetName(title)}`,
          });
          break;
        case RequestAction.Withdraw:
          await fsApi(CoreService.WithdrawPendingPermission, {
            permissionId,
          });
          addToast({
            messageBarType: MessageBarType.success,
            message: `You have Withdrawn the access request for ${getTargetName(title)}`,
          });
          break;
      }
      load();
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while applying ${actionType} request: `));
    } finally {
      setLoadingMessage('');
    }
  }, [addToast, load, title]);
  const onAction = useCallback(
    (action: RequestAction, item: AccessRequest) => {
      refMeta.current.accessRequest = item;
      refMeta.current.actionType = action;
      refMeta.current.dialogInfo = dialogActionMap.get(action);
      refMeta.current.reason = '';
      toggleHideDialog();
    },
    [toggleHideDialog, doAction]
  );
  const onChangeFilters = (_e: FormEvent<HTMLDivElement>, option?: IComboBoxOption) => {
    if (option) {
      const state = option.key as PermissionState;
      if (option.selected) {
        if (!filters.includes(state)) {
          setFilters([...filters, state]);
        }
      } else {
        if (filters.includes(state)) {
          setFilters(filters.filter((s) => s !== state));
        }
      }
    }
  };
  const detailsListProps: IDetailsListProps = {
    styles: detailsListStylesCard,
    headerStyles: detailsHeaderStylesBasic,
    columns: getColumns(subTitle, onAction),
    isHeaderVisible: false,
    items: data.items,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      if (item.isNotLoaded && !refMeta.current.isLoadingPage) {
        refMeta.current.isLoadingPage = true;
        onLoad(data, false);
      }
      return <ListRow data={item} columns={columns} loading={item.isNotLoaded} />;
    },
  };
  useEffect(() => {
    refMeta.current.mounted = true;
    load();
    return () => {
      refMeta.current.mounted = false;
    };
  }, [load]);
  const classNames = useClassNames<IAccessRequestListStyles, IAccessRequestListClassNames>(
    'AccessRequestList',
    accessRequestListStylesDefault
  );
  return (
    <div className={classNames.root} data-test="AccessRequestList">
      <div className={classNames.description}>
        <div className={classNames.descriptionText}>{description}</div>
        <div className={classNames.filter}>
          <Dropdown
            placeholder="Filter by status"
            data-test="dropdown-access-request-list-filter"
            multiSelect
            selectedKeys={filters}
            options={filterOptions}
            onChange={onChangeFilters}
          />
        </div>
      </div>
      {loadingMessage ? (
        <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
      ) : (
        <div className={classNames.cardView} data-is-scrollable="true" data-test="details-list-card-view">
          {data.items.length === 0 ? (
            <EmptyMessage message="No Items Available" />
          ) : (
            <DetailsList {...detailsListProps} />
          )}
        </div>
      )}
      <BaseDialog
        hidden={hideDialog}
        onDismiss={toggleHideDialog}
        title={`${refMeta.current.actionType} ${
          refMeta.current.actionType === RequestAction.Withdraw ? 'Access Request' : 'Permission'
        }`}
        content={
          <div>
            <MessageBar messageBarType={refMeta.current.dialogInfo?.messageBarType}>
              {refMeta.current.dialogInfo?.message(refMeta.current.accessRequest)}
            </MessageBar>
            {refMeta.current.actionType !== RequestAction.Withdraw && (
              <div style={{ marginTop: 16 }}>
                <TextField
                  placeholder="Reason"
                  multiline
                  onChange={(_e, value) => (refMeta.current.reason = value || '')}
                />
              </div>
            )}
          </div>
        }
        footer={
          <>
            <Link styles={[linkStylesGhost, { root: { marginRight: 8 } }]} onClick={toggleHideDialog}>
              Cancel
            </Link>
            <Link
              styles={
                refMeta.current.actionType === RequestAction.Reject
                  ? [linkStylesGhost, refMeta.current.dialogInfo?.linkStyles]
                  : linkStylesPrimary
              }
              onClick={() => {
                toggleHideDialog();
                doAction();
              }}
            >{`${refMeta.current.actionType} ${
              refMeta.current.actionType === RequestAction.Withdraw ? 'Access Request' : 'Permission'
            }`}</Link>
          </>
        }
      ></BaseDialog>
    </div>
  );
};
